// Header row of the filter drawers (CatalogPage's product/document drawer, SystemDetailContent's
// article-list drawer): heading on the left; on the right a "Zurücksetzen" link once anything is
// set, and the close icon.
//
// Rendered as the FIRST ELEMENT OF THE DRAWER BODY, deliberately not through Mantine's own
// `title`/close-button header: under @uag/doka-design's `.slide-over` styling that header ended
// up out of the flow (review screenshots: title, reset and close icon printed over the first facet
// group, and it stayed that way whether the header was sticky or static - a fight against a
// stylesheet we can't inspect here). A plain row inside our own content is ordinary flow layout
// and cannot overlap anything. The drawers therefore render with `withCloseButton={false}` and
// but keep `title` for the dialog's accessible name (Mantine wires aria-labelledby to it) and
// hide that header visually with FILTER_DRAWER_HEADER_PROPS - inline styles, so App.css's own
// header rules can't resurrect it. Escape and click-outside still close them via Mantine, this
// row's icon does it by hand. The document drawer gets a reset it never had (Sprache/Typ/Edition)
// for free.

import { Box, CloseButton, Group, Text, UnstyledButton } from '@mantine/core';

import { useCatalogLabels } from '../labels';
import { TEXT_ACTION_CLASS } from './FacetGroup';

import type { ReactNode } from 'react';

/** Spread onto the Drawer next to `title`: no Mantine close button, Mantine header visually
 * hidden (still in the DOM, so the title keeps naming the dialog for assistive technology). */
export const FILTER_DRAWER_HEADER_PROPS = {
    withCloseButton: false,
    styles: {
        header: {
            position: 'absolute',
            width: 1,
            height: 1,
            margin: -1,
            padding: 0,
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            whiteSpace: 'nowrap',
            border: 0
        }
    }
} as const;

interface FilterDrawerHeaderProps {
    title: string;
    /** Whether anything is set - the reset button only shows when there is something to reset. */
    canReset: boolean;
    onReset: () => void;
    onClose: () => void;
    /** What the filters act on, shown under the heading - a system's Artikelliste uses it so its
     * drawer cannot be mistaken for the catalog's own (reported: "verwirrend für den Anwender"). */
    context?: ReactNode;
}

export const FilterDrawerHeader = ({ title, canReset, onReset, onClose, context }: FilterDrawerHeaderProps) => {
    const { L } = useCatalogLabels();
    // Heading left; "Zurücksetzen" as a quiet text link next to the close icon on the right, and a
    // thin rule under the row - the outlined button beside the heading competed with it.
    return (
        <Box mb="md" pb="md" style={{ borderBottom: '1px solid var(--pih-border)' }}>
            <Group align="center" justify="space-between" mb={context ? 'sm' : 0} wrap="nowrap">
                <Text component="h2" fw={700} m={0} size="lg" style={{ color: 'var(--pih-text)' }}>
                    {title}
                </Text>
                <Group align="center" gap="md" wrap="nowrap">
                    {canReset && (
                        <UnstyledButton className={TEXT_ACTION_CLASS} onClick={onReset}>
                            {L.clearFilters}
                        </UnstyledButton>
                    )}
                    <CloseButton aria-label={L.close} size="md" onClick={onClose} />
                </Group>
            </Group>
            {context}
        </Box>
    );
};
